import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Moon, ArrowLeft, ArrowRight, ChevronRight, BookOpen, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchKBArticle } from "@/lib/api";
import NavBar from "@/components/NavBar";

const CATEGORY_ACCENTS = {
  planets: "text-amber-400",
  houses: "text-indigo-400",
  signs: "text-emerald-400",
  nakshatras: "text-pink-400",
  foundations: "text-cyan-400",
  "lunar-nodes": "text-violet-400",
  "planetary-strength": "text-orange-400",
  yogas: "text-purple-400",
  aspects: "text-sky-400",
  dashas: "text-rose-400",
  transits: "text-red-400",
  vargas: "text-emerald-400",
  "nakshatra-padas": "text-teal-400",
  "karmic-astrology": "text-violet-400",
};

const CATEGORY_BORDER = {
  planets: "border-amber-500/20",
  houses: "border-indigo-500/20",
  signs: "border-emerald-500/20",
  nakshatras: "border-pink-500/20",
  foundations: "border-cyan-500/20",
  "lunar-nodes": "border-violet-500/20",
  "planetary-strength": "border-orange-500/20",
  yogas: "border-purple-500/20",
  aspects: "border-sky-500/20",
  dashas: "border-rose-500/20",
  transits: "border-red-500/20",
  vargas: "border-emerald-500/20",
  "nakshatra-padas": "border-teal-500/20",
  "karmic-astrology": "border-violet-500/20",
};

export default function ArticleDetail() {
  const { articleSlug } = useParams();
  const [article, setArticle] = useState(null);
  const [prev, setPrev] = useState(null);
  const [next, setNext] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    fetchKBArticle(articleSlug)
      .then((d) => {
        setArticle(d.article);
        setPrev(d.prev);
        setNext(d.next);
        setRelated(d.related || []);
        window.scrollTo(0, 0);
      })
      .catch(() => navigate("/learn"))
      .finally(() => setLoading(false));
  }, [articleSlug, navigate]);

  if (loading || !article) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <img src="/logo.png" alt="Daily Nakshatra" className="h-10 w-auto logo-pulse" />
      </div>
    );
  }

  const accent = CATEGORY_ACCENTS[article.category_slug] || "text-white/70";
  const borderAccent = CATEGORY_BORDER[article.category_slug] || "border-white/10";

  return (
    <div className="min-h-screen bg-[#050505]">
      <NavBar />

      <main className="pt-14 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 pt-8 pb-2 text-xs text-white/30 font-[Manrope] flex-wrap" data-testid="article-breadcrumb">
          <Link to="/learn" className="hover:text-white/60 transition-colors">Learn</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/learn/${article.category_slug}`} className={`hover:text-white/60 transition-colors ${accent}`}>
            {article.category_slug.charAt(0).toUpperCase() + article.category_slug.slice(1)}
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white/50 truncate max-w-[200px]">{article.title}</span>
        </div>

        {/* Back button */}
        <Button
          variant="ghost" size="sm"
          className="text-white/30 hover:text-white/60 mb-6 -ml-2 text-xs"
          onClick={() => navigate(`/learn/${article.category_slug}`)}
          data-testid="article-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to list
        </Button>

        {/* Article Header */}
        <header className="mb-10" data-testid="article-header">
          <h1 className="text-3xl sm:text-4xl font-light text-white/95 mb-2 font-[Manrope]">{article.title}</h1>
          <p className="text-base sm:text-lg text-white/40 font-[Manrope]">{article.subtitle}</p>
          {article.keywords && article.keywords.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {article.keywords.map((kw) => (
                <span key={kw} className="text-[11px] px-2.5 py-1 rounded-full border border-white/8 text-white/30 font-[Manrope] flex items-center gap-1">
                  <Tag className="w-2.5 h-2.5" />{kw}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* Article Content */}
        <article
          className="kb-article-content mb-12"
          dangerouslySetInnerHTML={{ __html: article.content_html }}
          data-testid="article-content"
        />

        {/* Prev / Next Navigation */}
        <div className={`flex items-stretch gap-4 mb-12 border-t ${borderAccent} pt-8`} data-testid="article-prev-next">
          {prev ? (
            <Link
              to={`/learn/article/${prev.slug}`}
              className="flex-1 group p-4 rounded-xl border border-white/8 hover:border-white/15 bg-white/[0.02] transition-all"
              data-testid="article-prev-link"
            >
              <span className="text-[10px] uppercase tracking-wider text-white/25 font-[Manrope]">Previous</span>
              <div className="flex items-center gap-2 mt-1">
                <ArrowLeft className="w-3.5 h-3.5 text-white/25 group-hover:text-white/50 transition-colors" />
                <span className="text-sm text-white/70 group-hover:text-white/90 transition-colors font-[Manrope] truncate">{prev.title}</span>
              </div>
            </Link>
          ) : <div className="flex-1" />}
          {next ? (
            <Link
              to={`/learn/article/${next.slug}`}
              className="flex-1 group p-4 rounded-xl border border-white/8 hover:border-white/15 bg-white/[0.02] transition-all text-right"
              data-testid="article-next-link"
            >
              <span className="text-[10px] uppercase tracking-wider text-white/25 font-[Manrope]">Next</span>
              <div className="flex items-center justify-end gap-2 mt-1">
                <span className="text-sm text-white/70 group-hover:text-white/90 transition-colors font-[Manrope] truncate">{next.title}</span>
                <ArrowRight className="w-3.5 h-3.5 text-white/25 group-hover:text-white/50 transition-colors" />
              </div>
            </Link>
          ) : <div className="flex-1" />}
        </div>

        {/* Related Articles */}
        {related.length > 0 && (
          <div className="mb-20" data-testid="article-related">
            <h3 className="text-xs uppercase tracking-[0.2em] text-white/25 mb-4 font-[Manrope] flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5" /> Related Articles
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/learn/article/${r.slug}`}
                  className="group flex items-center gap-3 p-3 rounded-xl border border-white/6 hover:border-white/15 bg-white/[0.02] transition-all"
                  data-testid={`related-${r.slug}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white/80 group-hover:text-white/95 font-[Manrope] truncate">{r.title}</p>
                    <p className="text-xs text-white/30 font-[Manrope]">{r.subtitle}</p>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-white/15 shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
